import { View } from "@react-pdf/renderer";
import ContractStyles from "../assets/styles/contractStyles";
import ContractListItem from "../constants/listItem";
import RenderClauses from "../constants/renderClauses";
import { formatSerialNumber, numberClauseTree } from "../constants/serialNumbers";
import { getPermittedActivitiesInfo } from "../../contractDetails/contractDetailsDefaults";

const getActivityText = (label) => {
  const map = {
    "Investment Arrangements":
      "Making arrangements for individuals to enter into, vary, or carry out investment transactions with counterparties, within the meaning of article 25 of the Regulated Activities Order, and giving advice on the merits of entering into investment transactions as permitted by article 53(1) of the Regulated Activities Order.",
    "Insurance-Related Activities":
      "Arranging, assisting in the administration, and performance of insurance contracts, and providing related advice, subject to the requirement that the Appointed Representative is included on the Financial Services Register for such activities.",
    "Home Finance Transactions":
      " Arranging or advising on home finance transactions for customers with counterparties and advising on variations of such transactions, including as specified under articles 53A, 53B, and 53C of the Regulated Activities Order.",
    "Equity Release Activities":
      "Advising on and arranging equity release products for customers, ensuring that such activities comply with applicable regulations and are conducted in a manner that prioritises the best interests of clients. This includes providing advice on the suitability of equity release options, ensuring transparency in terms of costs and risks associated with these products, and confirming that the Appointed Representative is registered on the Financial Services Register for such activities as required under FCA regulations.",
    "Long-Term Care Planning":
      " Advising on and arranging financial products related to long-term care, including insurance policies and investment solutions designed to fund care needs. The Appointed Representative shall ensure that all advice is tailored to meet the specific needs and circumstances of clients, including assessments of affordability and suitability, and will operate within the frameworks set out by the FCA regarding long-term care provision. The Appointed Representative must also be duly registered on the Financial Services Register for engaging in such activities.",
  };

  return map[label] || "";
};

const SalesActivities = ({ contractDetails = {}, serialNumber = 12 }) => {
  const styles = ContractStyles();
  const activityInfo = getPermittedActivitiesInfo(
    contractDetails.permittedActivities || [],
  );


  const dynamicActivityClauses = activityInfo.activityLabels.map(
    (label, index) => ({
      content: `${label}: ${getActivityText(label)}`,
    }),
  );

  const obligations = [
    {
      //   title: "",
      title:
        "The Appointed Representative must use the prescribed electronic sales process to conduct the business with the Company. The Company will only process plan applications and pay commission on business submitted using the electronic sales process. ",
    },
    {
      title: "Permitted Activities",
      content:
        "The Appointed Representative is authorised to carry out specific activities on behalf of the Firm, limited to those specified in this Agreement, and subject to applicable FCA regulations and guidance. The Appointed Representative's activities and responsibilities are governed by the guidelines established in SUP 12.5, ensuring compliance with the standards set forth by the FCA.\nThese activities are:",
      clauses: dynamicActivityClauses,
    },
    {
      title: "Limitations and Exclusivity Requirements",
      content: "",
      clauses: [
        {
          content:
            "Restriction on Multiple Principals: The Appointed Representative is prohibited from acting as an appointed representative for other firms in designated investment business, regulated mortgage activities (including lifetime mortgages), and MCD credit intermediation activities.",
        },
        {
          content:
            "Scope Limitation: The Appointed Representative shall only perform activities expressly permitted by this Agreement. Any additional activities must receive prior written approval from the Firm, in compliance with FCA regulations.",
        },
        {
          content:
            "Notice of Additional Appointments: The Appointed Representative shall notify the Firm of any intention to accept an appointment from another principal and must disclose the business and scope of such intended appointment.",
        },
      ],
    },
    {
      title: "Compliance with Relevant FCA Requirements",
      content: "",
      clauses: [
        {
          content:
            "FCA Guidance Adherence: The Appointed Representative agrees to fully comply with all relevant FCA requirements under the Financial Services and Markets Act (FSMA) and related rules that govern the activities authorised by this Agreement.",
        },
        {
          content:
            "Cooperation and Disclosure Obligations: The Appointed Representative shall cooperate with the Firm in regulatory matters, including providing the Firm and the FCA access to premises, documents, and any other information required for compliance purposes as per SUP 2.3.4G and SUP 2.3.5R(2).",
        },
        {
          content:
            "Auditor Access: The Appointed Representative shall provide the Firm’s auditors with rights equivalent to those under section 341 of the Act, including access to relevant records and information as required.",
        },
      ],
    },
    {
      title: "Information Sharing",
      content: "",
      clauses: [
        {
          content:
            "Reporting and Notifications: The Appointed Representative shall provide timely information necessary for the Firm to fulfil reporting and notification obligations under SUP 12, including any information related to compliance, significant matters, or changes in activities that may impact regulatory obligations.",
        },
        {
          content:
            "Regulatory Changes: The Appointed Representative shall notify the Firm of any changes to regulatory requirements or activities that may affect their permitted activities or the Firm’s compliance with FCA rules.",
        },
      ],
    },
    {
      title: "Termination and Oversight Provisions",
      content: "",
      clauses: [
        {
          content:
            "Termination Rights: The Firm reserves the right to terminate this Agreement if it is determined that effective oversight of the Appointed Representative’s activities can no longer be maintained, or if regulatory compliance issues arise that cannot be resolved.",
        },
        {
          content:
            "Continuous Oversight Obligation: The Appointed Representative acknowledges that the Firm has an ongoing obligation to ensure compliance and reserves the right to impose restrictions on the Appointed Representative’s activities to ensure compliance with FCA rules. The Firm shall conduct regular compliance reviews of the Appointed Representative's activities, at a minimum frequency of quarterly, to ensure adherence to FCA regulations and the Firm's internal policies.",
        },
      ],
    },
  ];

  return (
    <View style={[styles.flexColumn, styles.gap_sm]} break>
      <ContractListItem
        id="sales-activities"
        marker={formatSerialNumber(serialNumber)}
        markerWidth={0}
        styles={styles}
        textStyle={[styles.contentSemibold, styles.text_primary]}
      >
        SCHEDULE TWO – Sales Activities
      </ContractListItem>

      <View style={[styles.flexColumn, styles.gap_sm]}>
        {numberClauseTree(obligations, serialNumber).map(({ number, title, content, clauses }) => (
          <View
            key={number}
            style={[
              styles.flexColumn,
              styles.gap_xs,
              // { paddingTop: number === "4.5." ? styles.py_lg : styles.py_md },
            ]}
          >
            <ContractListItem
              marker={number}
              markerWidth={24}
              styles={styles}
              textStyle={styles.buttonTextSmall}
            >
              {title}
            </ContractListItem>
            {content && (
              <ContractListItem
                marker=""
                markerWidth={0}
                styles={styles}
                textStyle={styles.buttonTextSmall}
              >
                {content}
              </ContractListItem>
            )}

            <RenderClauses
              clauses={clauses}
              styles={styles}
              number={number}
              getTextStyle={(content, number, level) =>
                level === 0 &&
                activityInfo.activityLabels.some((label) =>
                  content.startsWith(label),
                )
                  ? [styles.buttonTextSmall, styles.text_red]
                  : styles.buttonTextSmall
              }
            />
          </View>
        ))}
      </View>
    </View>
  );
};

export default SalesActivities;
